import {safeLocalStorage} from '@utils/safeStorages';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

/** wrapper around useState that syncronizes it to localStorage */
const useLocalStorageState = <Type>({
  defaultValue,
  storageKey,
}: {
  /** default state if nothing was saved or failed to parse saved state */
  defaultValue: Type;
  /** key of localStorage item */
  storageKey: string;
}): [Type, Dispatch<SetStateAction<Type>>] => {
  const [value, setValueBase] = useState<Type>(defaultValue);

  useEffect(() => {
    const savedState = safeLocalStorage?.getItem(storageKey);

    if (!savedState) {
      return;
    }

    try {
      const value: Type = JSON.parse(savedState);
      setValueBase(value);
    } catch (e) {
      console.warn(
        `Failed to parse value = ${savedState} for key = ${storageKey}`,
      );
    }
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback(
    (updater: SetStateAction<Type>) => {
      setValueBase((current) => {
        let newValue: Type;

        if (updater instanceof Function) {
          newValue = updater(current);
        } else {
          newValue = updater;
        }

        if (safeLocalStorage !== null) {
          safeLocalStorage.setItem(storageKey, JSON.stringify(newValue));
        } else {
          console.error('localStorage is not available');
        }

        return newValue;
      });
    },
    [storageKey],
  );

  return [value, setValue];
};

export default useLocalStorageState;
